import { ActivityCompletionStatus, type Driver, type NewRouteReport, type ReportActivity, type TimeWindow } from '@cobliteam/api-lib';
import type { VehicleDto } from '@cobliteam/api-lib/src/__generated__/graphql';
import { newDestinationToFormattedAddressMapper } from '@cobliteam/api-lib/src/dashboard/routing/ObjectMappers';
import type { DateRange, TimeIntervalInMillis, TimestampInMillis } from '@cobliteam/common-types';
import type { Workbook, Worksheet } from 'exceljs';
import moment from 'moment';

import type { RoutingSpreadsheetNoPermissionMessage } from '../permissions';

const HEADER_ROW_WITH_DATE_INFO = 1;
const FIRST_ROW_AFTER_TEMPLATE_HEADER = 4;

export class ActivitySpreadsheetReportBuilder {
  public buildReport = async (spreadsheetTemplate: Blob, dateRange: DateRange, routesReport: NewRouteReport[]) => {
    const buffer = await this.readFile(spreadsheetTemplate);

    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();

    const file: Workbook = await workbook.xlsx.load(buffer as any);
    const sheet: any = file.getWorksheet(1);
    let rowIndex = 0;

    this.insertReportDate(sheet, dateRange);

    const sortedRoutesReport = routesReport.sort((a, b) => (b?.startTime?.planned || 0) - (a?.startTime?.planned || 0));

    sortedRoutesReport.map((row) => {
      const activities = this.rowTransformer(row);
      activities.map((activity) => {
        sheet.insertRow(FIRST_ROW_AFTER_TEMPLATE_HEADER + rowIndex, activity).alignment = {
          vertical: 'middle',
          horizontal: 'center',
        };
        rowIndex++;
      });
    });

    sheet.eachRow({ includeEmpty: true }, (row: any) => {
      row.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    const excelBuffer = await workbook.xlsx.writeBuffer();
    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
  };

  private formatDate = (date?: TimestampInMillis) => (date ? moment(date).format('DD/MM/YYYY') : '-');

  private formatHeaderDate = (dateRange: DateRange) => {
    const { start, end } = dateRange;

    const startDate = this.formatDate(start);
    const endDate = this.formatDate(end);
    return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
  };

  private getDateHour = (date?: TimestampInMillis) => (date ? moment(date).format('HH:mm') : '-');

  private insertReportDate = (worksheet: Worksheet, dateRange: DateRange) => {
    const headerValue = worksheet.getRow(HEADER_ROW_WITH_DATE_INFO).getCell(1);
    worksheet.getRow(1).getCell(1).value = `${headerValue} • ${this.formatHeaderDate(dateRange)}`;
  };

  private translateServiceStatus = (status?: ActivityCompletionStatus) => {
    switch (status) {
      case ActivityCompletionStatus.COMPLETED:
        return 'REALIZADO';
      case ActivityCompletionStatus.NOT_COMPLETED:
        return 'NÃO REALIZADO';
      default:
        return '-';
    }
  };

  private formatVehicleInfo = (vehicle?: VehicleDto) => {
    if (vehicle) {
      return vehicle.licensePlate ? vehicle.licensePlate : 'Veículo não identificado';
    }

    return 'Nenhum veículo alocado';
  };

  private formatDriverInfo = (driver?: Driver) => (driver ? driver.name : 'Nenhum motorista associado');

  private formatActivityName = (activity: ReportActivity) => activity.name || '-';

  private formatPhoneNumber = (phone?: string) => {
    if (phone) {
      const stripped = phone.replace(/[^\d]/g, '');
      const cutIndex = stripped.length === 11 ? 7 : 6;

      const prefix = stripped.slice(0, 2);
      const firstHalf = stripped.slice(2, cutIndex);
      const lastHalf = stripped.slice(cutIndex);

      return `(${prefix}) ${firstHalf}-${lastHalf}`;
    }

    return '-';
  };

  private formatInstructions = (instructions?: string) => instructions || '-';

  private formatTimeWindow = (timeWindow?: TimeWindow) => (timeWindow ? `${moment(timeWindow.earliest).format('HH:mm')} - ${moment(timeWindow.latest).format('HH:mm')}` : '-');

  private formatObservations = (observations?: string | RoutingSpreadsheetNoPermissionMessage) => observations || '-';

  private formatDurationToMinutes = (durationInMillis?: TimeIntervalInMillis) => (durationInMillis ? Math.round(moment.duration(durationInMillis).asMinutes()) : '-');

  private rowTransformer = (route: NewRouteReport) => {
    const { startTime, driver, name: routeName, vehicle, activities } = route;

    return activities.map((activity) => [
      startTime?.planned ? this.formatDate(startTime.planned) : this.formatDate(activity.startTime.planned),
      routeName || 'Atividade sem rota',
      this.formatVehicleInfo(vehicle || activity.vehicle),
      this.formatDriverInfo(driver || activity.driver),
      this.formatActivityName(activity),
      newDestinationToFormattedAddressMapper(activity.destination),
      this.formatPhoneNumber(activity.phoneNumber?.substring(2)),
      this.formatInstructions(activity.instructions),
      this.formatTimeWindow(activity?.timeWindows?.[0]),
      this.translateServiceStatus(activity.completionStatus),
      this.formatObservations(activity.observations),
    ]);
  };

  private readFile = (fileRes: Blob): Promise<Buffer> =>
    new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(fileRes);
      reader.onload = () => {
        resolve(reader.result as unknown as Buffer);
      };
    });
}
